import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faPlus,
  faChevronDown,
  faChevronUp,
  faTimes,
  faUpload,
  faCheck,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { getFolderDisplayLabel } from '../utils/folderHierarchy';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import { useChannelConfigs } from '../hooks/useChannelConfigs';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { isUploadCreativesHidden } from '../utils/uploadCreativesGating';
import './CampaignCreation.css';
import './CreativeManagerCreation.css';

/**
 * CreativeManagerCreation component for uploading creative assets
 * Supports multiple file types with automatic platform detection:
 * - Excel files with madhive/ad_tags → Madhive
 * - Excel files with sem/search/google → Google Ads Search
 * - ZIP files with dv360/natives → DV360
 * - ZIP files with _dsp/_ttd/ttd → The Trade Desk
 * - ZIP files with facebook/fb → Facebook
 * - ZIP files with reddit → Reddit
 * - Default Excel → Google Ads Search, Default ZIP → Facebook
 */
const CreativeManagerCreation = () => {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();

  // The hide-upload-creatives account setting removes every entry point to
  // this page, but it stays URL-reachable — bounce back to the job list so
  // the flag can't be bypassed by a bookmark or manually typed URL.
  const { settings: accountSettings } = useAccountSettings(accountId, user);
  const uploadCreativesHidden = isUploadCreativesHidden(accountSettings);
  useEffect(() => {
    if (uploadCreativesHidden) {
      navigate('/creative-manager', { replace: true });
    }
  }, [uploadCreativesHidden, navigate]);

  // Channel configs hook
  const { getChannelsForPlatform, hasMultipleChannels, isLoading: channelConfigsLoading } = useChannelConfigs(accountId, user);

  // Component state
  const [jobName, setJobName] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showNewFolderForm, setShowNewFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedChannelIds, setSelectedChannelIds] = useState({}); // { 'google-ads-search': 'channel-id', 'facebook': 'channel-id', ... }

  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Platform-specific fields that users need to enter
  // Note: campaign_id is no longer needed - creatives are assigned to ALL campaigns in the selected folder
  const [platformFields, setPlatformFields] = useState({
    'facebook': { ad_account_id: '', landing_page_url: '' },
    'google-ads-search': { customer_id: '' },
    'reddit': { profile_id: '', landing_page_url: '' },
    'ttd': { advertiser_id: '' },
    'dv360': { advertiser_id: '' },
    'madhive': { advertiser_id: '' }
  });

  // Folder search and pagination state
  const [folderSearchTerm, setFolderSearchTerm] = useState('');
  const [folderPage, setFolderPage] = useState(1);
  const [folderPagination, setFolderPagination] = useState(null);
  const [isLoadingMoreFolders, setIsLoadingMoreFolders] = useState(false);
  const folderDropdownRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const fileInputRef = useRef(null);

  // Detect platform from filename with comprehensive pattern matching
  const detectPlatform = (filename) => {
    const lowerFilename = filename.toLowerCase();
    const ext = lowerFilename.split('.').pop();

    // TXT files - Check for platform indicators
    if (ext === 'txt') {
      // Facebook indicators (dpupdate, dpupload, fb, meta)
      if (lowerFilename.includes('dpupdate') || lowerFilename.includes('dpupload') ||
          lowerFilename.includes('facebook') || lowerFilename.includes('fb_') ||
          lowerFilename.includes('_fb') || lowerFilename.includes('meta_')) {
        return 'facebook';
      }
      // Default TXT to madhive (Display tags)
      return 'madhive';
    }

    // Excel files - Check for specific platform indicators
    if (['xlsx', 'xls', 'xlsm'].includes(ext)) {
      // Madhive indicators
      if (lowerFilename.includes('madhive') || lowerFilename.includes('ad_tags')) {
        return 'madhive';
      }
      // Google Ads Search indicators
      if (lowerFilename.includes('sem') || 
          lowerFilename.includes('search') || 
          lowerFilename.includes('google') ||
          lowerFilename.includes('gads') ||
          lowerFilename.includes('keyword') ||
          lowerFilename.includes('kw_')) {
        return 'google-ads-search';
      }
      // Default Excel files to Google Ads Search
      return 'google-ads-search';
    }

    // ZIP files - Check for platform indicators
    if (ext === 'zip') {
      // DV360 indicators (check first as it's most specific)
      if (lowerFilename.includes('dv360') || 
          lowerFilename.includes('dv_360') ||
          lowerFilename.includes('display_video') ||
          lowerFilename.includes('natives')) {
        return 'dv360';
      }
      // TTD indicators
      if (lowerFilename.includes('ttd') || 
          lowerFilename.includes('_dsp') || 
          lowerFilename.includes('_ttd') ||
          lowerFilename.includes('trade_desk') ||
          lowerFilename.includes('tradedesk')) {
        return 'ttd';
      }
      // Facebook indicators
      if (lowerFilename.includes('facebook') || 
          lowerFilename.includes('_fb') || 
          lowerFilename.includes('fb_') ||
          lowerFilename.includes('meta')) {
        return 'facebook';
      }
      // Reddit indicators
      if (lowerFilename.includes('reddit')) {
        return 'reddit';
      }
      // Default ZIP to Facebook
      return 'facebook';
    }
    
    return 'unknown';
  };

  const getPlatformDisplayName = (platform) => {
    const names = {
      'google-ads-search': 'Google Ads Search',
      'facebook': 'Facebook',
      'reddit': 'Reddit',
      'ttd': 'The Trade Desk',
      'dv360': 'DV360',
      'madhive': 'Madhive',
      'unknown': 'Unknown'
    };
    return names[platform] || platform;
  };

  // Fetch folders
  useEffect(() => {
    if (accountId) fetchFolders();
  }, [accountId]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!showFolderDropdown) {
      setFolderSearchTerm('');
      setFolderPage(1);
    }
  }, [showFolderDropdown]);

  // Handle folder search with debounce
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFolderPage(1);
      fetchFolders(folderSearchTerm, 1);
    }, 300);
  }, [folderSearchTerm]);

  const fetchFolders = async (search = '', page = 1, append = false) => {
    if (!accountId) return;
    try {
      if (page === 1) setIsLoading(true);
      else setIsLoadingMoreFolders(true);

      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: {
          accountId,
          userId: user?.userId || user?.id,
          search: search || undefined,
          page,
          limit: 20,
          // Name sort groups each sub-folder under its parent (Market ->
          // Advertiser), matching the other creation-flow pickers
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });

      const foldersData = response.data.folders || response.data || [];
      setFolders(prev => append ? [...prev, ...foldersData] : foldersData);
      if (response.data.pagination) setFolderPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching folders:', err);
      if (page === 1) setError('Failed to load folders');
    } finally {
      setIsLoading(false);
      setIsLoadingMoreFolders(false);
    }
  };

  const loadMoreFolders = useCallback(() => {
    if (folderPagination?.hasNextPage && !isLoadingMoreFolders) {
      const nextPage = folderPage + 1;
      setFolderPage(nextPage);
      fetchFolders(folderSearchTerm, nextPage, true);
    }
  }, [folderPagination, isLoadingMoreFolders, folderPage, folderSearchTerm]);

  const handleFolderScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) loadMoreFolders();
  }, [loadMoreFolders]);

  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    setShowFolderDropdown(false);
    setShowNewFolderForm(false);
  };

  const handleCreateNewFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      setIsLoading(true);
      const response = await api.post(getApiUrl('/api/v2/folders'), {
        name: newFolderName.trim(),
        accountId,
        userId: user?.userId || user?.id
      });
      const newFolder = response.data.folder || response.data;
      setFolders(prev => [newFolder, ...prev]);
      setSelectedFolder(newFolder);
      setShowNewFolderForm(false);
      setNewFolderName('');
      setShowFolderDropdown(false);
    } catch (err) {
      console.error('Error creating folder:', err);
      setError(err.response?.data?.error || 'Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  // File handling
  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    e.target.value = ''; // Reset input
  };

  const processFiles = (files) => {
    const validExtensions = ['.xlsx', '.xls', '.xlsm', '.zip', '.txt'];
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return validExtensions.includes(ext);
    });

    if (validFiles.length !== files.length) {
      setError('Some files were skipped. Only Excel (.xlsx, .xls, .xlsm), ZIP, and TXT files are supported.');
    }

    const newFiles = validFiles.map(file => ({
      file,
      name: file.name,
      size: file.size,
      platform: detectPlatform(file.name)
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      // Check if channel configs are missing
      if (!areAllChannelConfigsSelected()) {
        setError('Please select channel configurations for all platforms');
      } else if (!areAllPlatformFieldsValid()) {
        setError('Please fill in all required platform-specific fields');
      }
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const userId = user?.userId || user?.id;

      const formData = new FormData();
      formData.append('name', jobName.trim());
      formData.append('folderId', selectedFolder.id);

      // Include channel IDs for each platform (JSON stringified)
      const platforms = getUniquePlatforms();
      const channelIds = {};
      platforms.forEach(platform => {
        const channels = getChannelsForPlatform(platform);
        if (channels.length === 1) {
          channelIds[platform] = channels[0].id;
        } else if (selectedChannelIds[platform]) {
          channelIds[platform] = selectedChannelIds[platform];
        }
      });
      formData.append('channelIds', JSON.stringify(channelIds));

      // Include platform-specific fields (only for platforms that have uploaded files)
      const platformFieldsToSend = {};
      platforms.forEach(platform => {
        if (platformFields[platform]) {
          platformFieldsToSend[platform] = platformFields[platform];
        }
      });

      // Debug: Log what we're sending
      console.log('[DEBUG-FE] platformFields state:', JSON.stringify(platformFields, null, 2));
      console.log('[DEBUG-FE] platforms with files:', platforms);
      console.log('[DEBUG-FE] platformFieldsToSend:', JSON.stringify(platformFieldsToSend, null, 2));
      if (platformFieldsToSend.facebook) {
        console.log('[DEBUG-FE] facebook.landing_page_url:', platformFieldsToSend.facebook.landing_page_url);
      }
      if (platformFieldsToSend.reddit) {
        console.log('[DEBUG-FE] reddit.landing_page_url:', platformFieldsToSend.reddit.landing_page_url);
      }

      formData.append('platformFields', JSON.stringify(platformFieldsToSend));

      uploadedFiles.forEach((fileData) => {
        formData.append('files', fileData.file);
      });

      // Pass userId and accountId as query params (required by extractUser middleware)
      const response = await api.post(getApiUrl('/api/v2/creative-manager'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { userId, accountId }
      });

      if (response.data.jobId) {
        navigate(`/creative-manager/${response.data.jobId}`);
      } else {
        navigate('/creative-manager');
      }
    } catch (err) {
      console.error('Error creating creative manager job:', err);
      setError(err.response?.data?.error || 'Failed to create job');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get unique platforms from uploaded files
  const getUniquePlatforms = useCallback(() => {
    const platforms = new Set();
    uploadedFiles.forEach(f => {
      if (f.platform && f.platform !== 'unknown') {
        platforms.add(f.platform);
      }
    });
    return Array.from(platforms);
  }, [uploadedFiles]);

  // Auto-select channel configs when files are uploaded
  useEffect(() => {
    const platforms = getUniquePlatforms();
    const newSelectedIds = { ...selectedChannelIds };
    let hasChanges = false;

    platforms.forEach(platform => {
      // Only auto-select if not already selected
      if (!newSelectedIds[platform]) {
        const channels = getChannelsForPlatform(platform);
        if (channels.length === 1) {
          // Auto-select if only one channel config
          newSelectedIds[platform] = channels[0].id;
          hasChanges = true;
        }
      }
    });

    // Clean up selections for platforms no longer in the file list
    Object.keys(newSelectedIds).forEach(platform => {
      if (!platforms.includes(platform)) {
        delete newSelectedIds[platform];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setSelectedChannelIds(newSelectedIds);
    }
  }, [uploadedFiles, getChannelsForPlatform, getUniquePlatforms, selectedChannelIds]);

  // Check if all platforms with multiple channel configs have a selection
  const areAllChannelConfigsSelected = useCallback(() => {
    const platforms = getUniquePlatforms();
    for (const platform of platforms) {
      if (hasMultipleChannels(platform) && !selectedChannelIds[platform]) {
        return false;
      }
    }
    return true;
  }, [getUniquePlatforms, hasMultipleChannels, selectedChannelIds]);

  // Validate that all required platform-specific fields are filled
  // Note: campaign_id is no longer required - creatives are assigned to ALL campaigns in the selected folder
  const areAllPlatformFieldsValid = useCallback(() => {
    const platforms = getUniquePlatforms();
    for (const platform of platforms) {
      const fields = platformFields[platform];
      if (!fields) continue;

      if (platform === 'facebook') {
        if (!fields.ad_account_id?.trim() || !fields.landing_page_url?.trim()) return false;
      } else if (platform === 'google-ads-search') {
        if (!fields.customer_id?.trim()) return false;
      } else if (platform === 'reddit') {
        if (!fields.profile_id?.trim() || !fields.landing_page_url?.trim()) return false;
      } else if (platform === 'ttd') {
        if (!fields.advertiser_id?.trim()) return false;
      } else if (platform === 'dv360') {
        if (!fields.advertiser_id?.trim()) return false;
      } else if (platform === 'madhive') {
        if (!fields.advertiser_id?.trim()) return false;
      }
    }
    return true;
  }, [getUniquePlatforms, platformFields]);

  const isFormValid = () => {
    return jobName.trim() && selectedFolder && uploadedFiles.length > 0 && areAllChannelConfigsSelected() && areAllPlatformFieldsValid();
  };

  // Get platform summary for uploaded files
  const getPlatformSummary = () => {
    const summary = {};
    uploadedFiles.forEach(f => {
      summary[f.platform] = (summary[f.platform] || 0) + 1;
    });
    return Object.entries(summary).map(([platform, count]) => ({
      platform,
      count,
      name: getPlatformDisplayName(platform)
    }));
  };

  // Get platforms that need channel config selection (multiple configs exist)
  const getPlatformsNeedingSelection = () => {
    return getUniquePlatforms().filter(platform => hasMultipleChannels(platform));
  };

  if (isLoading && folders.length === 0) {
    return (
      <div className="campaign-creation-container">
        <div className="loading-state"><p>Loading...</p></div>
      </div>
    );
  }


  return (
    <div className="campaign-creation-container">
      <div className="campaign-creation-header">
        <h1>Upload Creatives</h1>
        <p className="header-subtitle">Upload creative assets to distribute across platforms</p>
      </div>

      <form onSubmit={handleSubmit} className="campaign-creation-form">
        {error && (
          <div className="campaign-creation-error">
            <p>{error}</p>
            <button type="button" onClick={() => setError(null)}><FontAwesomeIcon icon={faTimes} /></button>
          </div>
        )}

        {/* Folder Selection */}
        <div className="form-group">
          <label id="folder-select-label">Select Folder *</label>
          <p className="form-help-text">
            <strong>Important:</strong> Creatives will be assigned to <strong>ALL campaigns</strong> in the selected folder that match the uploaded file platforms.
            For example, if you upload Facebook creatives, they will be assigned to all Facebook campaigns in the folder.
          </p>
          <div className="folder-select-container">
            <button type="button" className={`folder-select-button ${showFolderDropdown ? 'active' : ''}`}
              onClick={() => setShowFolderDropdown(!showFolderDropdown)}
              aria-labelledby="folder-select-label" aria-expanded={showFolderDropdown} aria-haspopup="listbox">
              <FontAwesomeIcon icon={faFolder} />
              <span>{selectedFolder ? getFolderDisplayLabel(selectedFolder) : 'Select a folder'}</span>
              <FontAwesomeIcon icon={showFolderDropdown ? faChevronUp : faChevronDown} className="dropdown-arrow" />
            </button>

            {showFolderDropdown && (
              <div className="folder-dropdown">
                <div className="folder-search-container">
                  <FontAwesomeIcon icon={faSearch} className="folder-search-icon" />
                  <input type="text" className="folder-search-input" placeholder="Search folders..."
                    value={folderSearchTerm} onChange={(e) => setFolderSearchTerm(e.target.value)} autoFocus />
                </div>
                <div className="folder-dropdown-list" ref={folderDropdownRef} onScroll={handleFolderScroll} role="listbox">
                  <button type="button" className="folder-dropdown-item create-new" onClick={() => setShowNewFolderForm(true)}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Create New Folder</span>
                  </button>
                  {showNewFolderForm && (
                    <div className="new-folder-inline-form">
                      <input type="text" placeholder="Folder name" value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateNewFolder(); }}} autoFocus />
                      <button type="button" onClick={handleCreateNewFolder} disabled={!newFolderName.trim()}>
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                      <button type="button" onClick={() => { setShowNewFolderForm(false); setNewFolderName(''); }}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}
                  {folders.map(folder => (
                    <button key={folder.id} type="button"
                      className={`folder-dropdown-item ${selectedFolder?.id === folder.id ? 'selected' : ''}`}
                      onClick={() => handleFolderSelect(folder)} role="option" aria-selected={selectedFolder?.id === folder.id}>
                      <FontAwesomeIcon icon={faFolder} />
                      <span>{getFolderDisplayLabel(folder)}</span>
                      {selectedFolder?.id === folder.id && <FontAwesomeIcon icon={faCheck} className="check-icon" />}
                    </button>
                  ))}
                  {isLoadingMoreFolders && <div className="folder-loading-more">Loading more...</div>}
                  {folders.length === 0 && !isLoading && <div className="folder-no-results">No folders found</div>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Job Name */}
        <div className="form-group">
          <label htmlFor="job-name">Job Name *</label>
          <input type="text" id="job-name" value={jobName} onChange={(e) => setJobName(e.target.value)}
            placeholder="e.g., Q1 2024 Campaign Creatives" className="form-input" />
        </div>

        {/* File Upload */}
        <div className="form-group">
          <label>Upload Files *</label>
          <p className="form-help-text">
            Drag and drop files or click to select. Supported formats: ZIP (for Facebook, Reddit, TTD, DV360), Excel (for Google Ads, Madhive CTV), and TXT (for Facebook ad tags, Madhive Display).
          </p>
          <div className={`file-upload-zone ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}>
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple
              accept=".xlsx,.xls,.xlsm,.zip,.txt" style={{ display: 'none' }} />
            <FontAwesomeIcon icon={faUpload} className="upload-icon" />
            <p>Drag files here or click to upload</p>
            <span className="upload-hint">ZIP for Facebook/Reddit/TTD/DV360, Excel for Google Ads/Madhive CTV, TXT for Facebook ad tags/Madhive Display</span>
          </div>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <div className="uploaded-files-list">
              <div className="uploaded-files-header">
                <h4>Uploaded Files ({uploadedFiles.length})</h4>
                {/* Platform Summary */}
                <div className="platform-summary">
                  {getPlatformSummary().map(({ platform, count, name }) => (
                    <span key={platform} className={`platform-tag ${platform}`}>{name}: {count}</span>
                  ))}
                </div>
              </div>
              {uploadedFiles.map((fileData, index) => (
                <div key={index} className="uploaded-file-item">
                  <div className="file-info">
                    <span className="file-name">{fileData.name}</span>
                    <span className="file-meta">
                      {formatFileSize(fileData.size)} • <span className={`platform-label ${fileData.platform}`}>{getPlatformDisplayName(fileData.platform)}</span>
                    </span>
                  </div>
                  <button type="button" className="remove-file-btn" onClick={() => removeFile(index)}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Channel Configuration Selection - Show for platforms with multiple configs */}
        {getPlatformsNeedingSelection().length > 0 && (
          <div className="form-group channel-config-section">
            <label>Channel Configuration *</label>
            <p className="form-help-text">
              Select which channel configuration to use for each platform.
            </p>
            {channelConfigsLoading ? (
              <p className="form-help-text">Loading channel configurations...</p>
            ) : (
              <div className="channel-config-grid">
                {getPlatformsNeedingSelection().map(platform => (
                  <div key={platform} className="channel-config-item">
                    <label htmlFor={`channel-config-${platform}`} className="channel-config-label">
                      {getPlatformDisplayName(platform)}
                    </label>
                    <select
                      id={`channel-config-${platform}`}
                      className="form-control"
                      value={selectedChannelIds[platform] || ''}
                      onChange={(e) => setSelectedChannelIds(prev => ({
                        ...prev,
                        [platform]: e.target.value
                      }))}
                      required
                    >
                      <option value="">Select a channel configuration</option>
                      {getChannelsForPlatform(platform).map(channel => (
                        <option key={channel.id} value={channel.id}>
                          {channel.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Platform-Specific Fields - Show when files are uploaded */}
        {getUniquePlatforms().length > 0 && (
          <div className="form-group platform-fields-section">
            <label>Platform Settings *</label>
            <p className="form-help-text">
              Enter the required settings for each platform. Creatives will be assigned to <strong>all campaigns</strong> in the selected folder automatically.
            </p>
            <div className="platform-fields-grid">
              {/* Facebook Fields */}
              {getUniquePlatforms().includes('facebook') && (
                <div className="platform-fields-card">
                  <h4 className="platform-fields-title">Facebook</h4>
                  <div className="platform-field">
                    <label htmlFor="facebook-ad-account-id">Ad Account ID *</label>
                    <input
                      type="text"
                      id="facebook-ad-account-id"
                      className="form-input"
                      placeholder="e.g., act_123456789"
                      value={platformFields['facebook']?.ad_account_id || ''}
                      onChange={(e) => setPlatformFields(prev => ({
                        ...prev,
                        'facebook': { ...prev['facebook'], ad_account_id: e.target.value }
                      }))}
                      required
                    />
                  </div>
                  <div className="platform-field">
                    <label htmlFor="facebook-landing-url">Landing Page URL *</label>
                    <input
                      type="url"
                      id="facebook-landing-url"
                      className="form-input"
                      placeholder="e.g., https://example.com/landing"
                      value={platformFields['facebook']?.landing_page_url || ''}
                      onChange={(e) => setPlatformFields(prev => ({
                        ...prev,
                        'facebook': { ...prev['facebook'], landing_page_url: e.target.value }
                      }))}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Google Ads Search Fields */}
              {getUniquePlatforms().includes('google-ads-search') && (
                <div className="platform-fields-card">
                  <h4 className="platform-fields-title">Google Ads Search</h4>
                  <div className="platform-field">
                    <label htmlFor="google-ads-customer-id">Customer ID *</label>
                    <input
                      type="text"
                      id="google-ads-customer-id"
                      className="form-input"
                      placeholder="e.g., 1234567890"
                      value={platformFields['google-ads-search']?.customer_id || ''}
                      onChange={(e) => setPlatformFields(prev => ({
                        ...prev,
                        'google-ads-search': { ...prev['google-ads-search'], customer_id: e.target.value }
                      }))}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Reddit Fields */}
              {getUniquePlatforms().includes('reddit') && (
                <div className="platform-fields-card">
                  <h4 className="platform-fields-title">Reddit</h4>
                  <div className="platform-field">
                    <label htmlFor="reddit-profile-id">Profile ID *</label>
                    <input
                      type="text"
                      id="reddit-profile-id"
                      className="form-input"
                      placeholder="e.g., t2_abc123"
                      value={platformFields['reddit']?.profile_id || ''}
                      onChange={(e) => setPlatformFields(prev => ({
                        ...prev,
                        'reddit': { ...prev['reddit'], profile_id: e.target.value }
                      }))}
                      required
                    />
                  </div>
                  <div className="platform-field">
                    <label htmlFor="reddit-landing-url">Landing Page URL *</label>
                    <input
                      type="url"
                      id="reddit-landing-url"
                      className="form-input"
                      placeholder="e.g., https://example.com/landing"
                      value={platformFields['reddit']?.landing_page_url || ''}
                      onChange={(e) => setPlatformFields(prev => ({
                        ...prev,
                        'reddit': { ...prev['reddit'], landing_page_url: e.target.value }
                      }))}
                      required
                    />
                  </div>
                </div>
              )}

              {/* TTD Fields */}
              {getUniquePlatforms().includes('ttd') && (
                <div className="platform-fields-card">
                  <h4 className="platform-fields-title">The Trade Desk</h4>
                  <div className="platform-field">
                    <label htmlFor="ttd-advertiser-id">Advertiser ID *</label>
                    <input
                      type="text"
                      id="ttd-advertiser-id"
                      className="form-input"
                      placeholder="e.g., 12345678"
                      value={platformFields['ttd']?.advertiser_id || ''}
                      onChange={(e) => setPlatformFields(prev => ({
                        ...prev,
                        'ttd': { ...prev['ttd'], advertiser_id: e.target.value }
                      }))}
                      required
                    />
                  </div>
                </div>
              )}

              {/* DV360 Fields */}
              {getUniquePlatforms().includes('dv360') && (
                <div className="platform-fields-card">
                  <h4 className="platform-fields-title">DV360</h4>
                  <div className="platform-field">
                    <label htmlFor="dv360-advertiser-id">Advertiser ID *</label>
                    <input
                      type="text"
                      id="dv360-advertiser-id"
                      className="form-input"
                      placeholder="e.g., 9876543210"
                      value={platformFields['dv360']?.advertiser_id || ''}
                      onChange={(e) => setPlatformFields(prev => ({
                        ...prev,
                        'dv360': { ...prev['dv360'], advertiser_id: e.target.value }
                      }))}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Madhive Fields */}
              {getUniquePlatforms().includes('madhive') && (
                <div className="platform-fields-card">
                  <h4 className="platform-fields-title">Madhive</h4>
                  <div className="platform-field">
                    <label htmlFor="madhive-advertiser-id">Advertiser ID *</label>
                    <input
                      type="text"
                      id="madhive-advertiser-id"
                      className="form-input"
                      placeholder="e.g., madhive_123456"
                      value={platformFields['madhive']?.advertiser_id || ''}
                      onChange={(e) => setPlatformFields(prev => ({
                        ...prev,
                        'madhive': { ...prev['madhive'], advertiser_id: e.target.value }
                      }))}
                      required
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/creative-manager')}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={!isFormValid() || isSubmitting}>
            {isSubmitting ? 'Uploading...' : 'Upload Creatives'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreativeManagerCreation;

